import React, { useRef } from 'react';
import { useScrollParent } from './useScrollParent';
import { useScrollProgress } from './useScrollProgress';
import { useInteractionDetection } from './useInteractionDetection';
import { useNonScrollableReveal } from './useNonScrollableReveal';
import { isScrollable } from './scrollUtils';

export interface ParallaxFooterProps {
  children: React.ReactNode;
}

/**
 * ParallaxFooter Component
 *
 * A footer that reveals itself with a parallax effect as the user scrolls down the page.
 *
 * Features:
 * - Smooth reveal animation based on scroll progress
 * - Works with both window scroll and scrollable containers
 * - Automatically reveals on non-scrollable pages after user interaction
 * - Responsive to content and viewport size changes
 *
 * The footer is rendered twice:
 * 1. An invisible placeholder to reserve space in the layout
 * 2. A fixed positioned version that animates into view
 */
export function ParallaxFooter({ children }: ParallaxFooterProps) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const scrollParent = useScrollParent(placeholderRef);
  const scrollProgress = useScrollProgress(scrollParent);
  const hasInteracted = useInteractionDetection(scrollParent);
  const nonScrollableReveal = useNonScrollableReveal(
    scrollParent,
    hasInteracted
  );

  // Determine which progress value to use
  const shouldUseNonScrollableReveal =
    scrollParent && !isScrollable(scrollParent);
  const finalProgress = shouldUseNonScrollableReveal
    ? nonScrollableReveal
    : scrollProgress;

  // Calculate transform
  // finalProgress is already 0-1, use it directly for translate percentage
  const footerTranslate = finalProgress;

  return (
    <div className="tailwind-root contents">
      {/* Invisible placeholder to reserve space in layout */}
      <div ref={placeholderRef} className="invisible pointer-events-none">
        {children}
      </div>

      {/* Fixed positioned footer that animates into view */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40"
        style={{
          transform: `translateY(${(1 - footerTranslate) * 100}%)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
}
